import React, { Suspense } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";

// Components
import {
  SidebarProvider,
  SidebarInset,
  readSidebarStateCookie,
} from "components/common/Sidebar/Sidebar";
import {
  sidebarShellChromeClassName,
  sidebarShellContentPanelClassName,
} from "components/layout/shell/sidebarShell";
import { AppUpdateBanner } from "components/common/AppUpdateBanner";
import { AppHeader } from "components/layout/shell/AppHeader";
import { ProjectAreaSidebar } from "components/layout/shell/AppSidebar";
import { GlobalNavigationSearchProvider } from "components/layout/shell/GlobalNavigationSearch";
import { ProjectHeaderActions } from "./ProjectHeaderActions";
import { ProjectHeaderBreadcrumb } from "./ProjectHeaderBreadcrumb";
import { ProjectLoadGate } from "./ProjectLoadGate";
import RouteFallback from "routes/RouteFallback";
import { ProjectCommentsChromeHost } from "features/project-comments/project-comments-chrome";
import { ProjectCommentsShell } from "features/project-comments/ProjectCommentsShell";
import { ProjectScriptAiHubProvider } from "features/script-editor/script-ai-hub";
import { ActivationChecklist } from "components/onboarding/ActivationChecklist";
import { DemoProjectBanner } from "components/onboarding/DemoProjectBanner/DemoProjectBanner";

// Config
import { PATH_DASHBOARD } from "config";
import { useProjectsApi } from "hooks/useProjects";
import { useWorkspaceIdentifiers } from "hooks/useWorkspace";
import { useSessionUser } from "hooks/useSession";
import { cn } from "utils/cn";

export const ProjectLayout = () => {
  const location = useLocation();
  const { data: sessionUser, isLoading: isSessionLoading } = useSessionUser();
  const { projects, isLoading: projectsLoading } = useProjectsApi({ take: 10, order: "DESC" });
  const { workspaceId } = useWorkspaceIdentifiers();

  if (!sessionUser || isSessionLoading || projectsLoading) {
    return <RouteFallback containerClassName="h-full min-h-[calc(100svh-54px)]" />;
  }

  const isProfileDeactivated = !sessionUser.is_active;

  if (isProfileDeactivated) {
    return <Navigate to={PATH_DASHBOARD.activateAccount} replace />;
  }

  /** Workspace projects grid at `/workspaces/:id/projects` (no `:projectId` segment). */
  const isWorkspaceProjectsListPage = /^\/workspaces\/[^/]+\/projects$/.test(location.pathname);

  // Redirect onboarding to projects page (onboarding is now integrated into projects page)
  if (location.pathname === PATH_DASHBOARD.onboarding) {
    if (!workspaceId) {
      return <Navigate to="/" replace />;
    }
    return <Navigate to={`/workspaces/${workspaceId}/projects`} replace />;
  }

  if (
    projects.length > 0 &&
    location.pathname === PATH_DASHBOARD.dashboard &&
    !isWorkspaceProjectsListPage
  ) {
    // If no workspace ID available, redirect to root to let workspace resolution happen
    if (!workspaceId) {
      return <Navigate to="/" replace />;
    }

    // Redirect to projects page instead of directly to first project
    return <Navigate to={`/workspaces/${workspaceId}/projects`} replace />;
  }

  // The Run AI hub wraps the whole shell, so the header and any page can open it.
  return (
    <ProjectScriptAiHubProvider>
      <SidebarProvider defaultOpen={readSidebarStateCookie() ?? true}>
        <GlobalNavigationSearchProvider>
          <ProjectAreaSidebar />
          <SidebarInset className="flex h-full min-h-0 flex-col overflow-hidden">
            <div className={sidebarShellChromeClassName}>
              <AppUpdateBanner />
              <ProjectCommentsChromeHost>
                <ProjectCommentsShell>
                  <AppHeader
                    breadcrumb={<ProjectHeaderBreadcrumb />}
                    actions={<ProjectHeaderActions />}
                  />
                  <div className={cn(sidebarShellContentPanelClassName, "overflow-auto")}>
                    {!isWorkspaceProjectsListPage && <ActivationChecklist />}
                    {!isWorkspaceProjectsListPage && <DemoProjectBanner />}
                    <Suspense
                      fallback={
                        <RouteFallback containerClassName="h-full min-h-[calc(100svh-54px)]" />
                      }
                    >
                      <ProjectLoadGate>
                        <Outlet />
                      </ProjectLoadGate>
                    </Suspense>
                  </div>
                </ProjectCommentsShell>
              </ProjectCommentsChromeHost>
            </div>
          </SidebarInset>
        </GlobalNavigationSearchProvider>
      </SidebarProvider>
    </ProjectScriptAiHubProvider>
  );
};
